import { patientSessionApi } from '../api';

/**
 * As duas etapas do vínculo de WhatsApp do paciente (tarefa 0.34).
 *
 * ⚠️ **As rotas não são novas** — são as mesmas que o `cinco-b2c` já usa
 * (`POST /auth/whatsapp/link-request` e `link-confirm`). O que a 0.34 mudou foi o **portador
 * da sessão**: enquanto o aplicativo não está publicado, quem chama é esta tela, com o token
 * que `POST /patients/activation` devolve.
 *
 * É o `link-confirm` que grava o binding **e registra o paciente no `cinco_chat`** — sem ele
 * a CintIA não conhece a pessoa e nem o `/auth/token-exchange` funciona para ela.
 *
 * O token vai explícito no header porque a instância é a `patientSessionApi`, sem
 * interceptor; o motivo está no comentário dela, em `services/api.ts`.
 */

const authorization = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

interface RequestLinkRequest {
  /** A sessão de paciente devolvida pela ativação — não o token do convite. */
  token: string;
  /** Qualquer máscara serve: o back normaliza para E.164 no `celebrate` (`whatsappJoi`). */
  whatsappNumber: string;
}

interface ConfirmLinkRequest {
  token: string;
  /** Os 6 dígitos que chegaram por WhatsApp. Valem 10 minutos. */
  code: string;
}

export const requestWhatsappLink = async ({
  token,
  whatsappNumber,
}: RequestLinkRequest): Promise<void> => {
  await patientSessionApi.post(
    '/auth/whatsapp/link-request',
    { whatsappNumber },
    authorization(token),
  );
};

export const confirmWhatsappLink = async ({
  token,
  code,
}: ConfirmLinkRequest): Promise<void> => {
  await patientSessionApi.post(
    '/auth/whatsapp/link-confirm',
    { code },
    authorization(token),
  );
};

/**
 * Os dois passos da **retentativa** de vínculo (tarefa 0.35), para quem pulou o passo na
 * ativação: pedir o link por e-mail e trocá-lo por um token restrito.
 *
 * ⚠️ **O que autoriza é a posse da caixa de e-mail, e nada mais** — sem senha e sem segundo
 * fator. Posse da caixa já converte em tomada de conta hoje (`/password/forgot` aceita
 * paciente), então confiar nela para vincular um número não inventa raiz de confiança nova.
 */

/**
 * ⚠️ **Não devolve nada e não falha por conta inexistente**: o back responde 204 em todos os
 * casos, de propósito, para a rota não virar oráculo de "esta pessoa tem cadastro aqui". A
 * tela **não pode** dizer "e-mail não encontrado" — ela diz "se existir uma conta ativada
 * com esse e-mail, o link chega em instantes".
 */
export const sendWhatsappLinkEmail = async (email: string): Promise<void> => {
  await patientSessionApi.post('/patients/whatsapp-link/request', { email });
};

export interface WhatsappLinkSession {
  name: string;
  /** Token restrito ao vínculo, 15 minutos. Não abre nenhuma outra rota. */
  token: string;
}

/** Consome o link do e-mail — **uso único** — e devolve o token restrito. */
export const startWhatsappLinkSession = async (
  token: string,
): Promise<WhatsappLinkSession> => {
  const { data } = await patientSessionApi.post(
    '/patients/whatsapp-link/session',
    { token },
  );

  return data;
};
